/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import { RiErrorWarningFill } from "react-icons/ri";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FileManagerAsPopup } from "@Features/files";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { getViewerUrl } from "@Helpers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BsThreeDotsVertical } from "react-icons/bs";
import { createMergeFile, refreshFileManager, getFileVersions } from "@Actions/fileUpload";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { conversionStatusTemplate, isMergeAllowed } from "@CubeComponents/constants/globalFunctions";
import TreeViewComponent from "@CubeComponents/html/TreeView";
import { fileFormatNotSupportedToast } from "@Actions/GlobalSearchAction";
import { useNavigate } from "react-router-dom";
import { constituentFilesOfMergedFileUrl } from "@Services/dataManager";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { Formatters } from "slickgrid-react";

/**
 * A constituent row of a merged model. `__parentId` is the tree plugin's own parent marker (top-level
 * rows have none), which is how this panel separates direct constituents from nested ones.
 */
type ConstituentRow = SelectedFileItem & {
  __parentId?: string | null;
  fileName?: string;
  fileVersionNumber?: number;
  isDeleted?: number;
  conversionStatus?: string;
  project?: { _id?: ObjectIdString };
};

/**
 * The `_id` an optimistic post-merge row is keyed by.
 *
 * The tree matches children to parents through `_id`/`__parentId`, and the value on hand at that
 * point is the selected file's `fileVersion` — which `folderItemSchema` declares as a bare `string`
 * even though it holds a file-version ObjectId. Parsed rather than branded blind, so a value that is
 * not an ObjectId is left unset instead of becoming a key nothing can ever match.
 */
const asRowId = (value: string | undefined): ObjectIdString | undefined => {
  const parsed = objectIdString.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

/**
 * Read the `{ data: { status, errorMessage } }` envelope every `/fileUpload` call answers with.
 *
 * A real runtime read, not an assertion: `createMergeFile` is declared `Promise<unknown>` in
 * `@Actions/fileUpload`, so nothing but a check can establish the shape. (The file manager keeps its
 * own copy of this reader for the same reason — see `readStatusEnvelope` in `useFileManager`.)
 */
const readStatusEnvelope = (response: unknown): { status?: boolean; errorMessage?: string } => {
  if (typeof response !== "object" || response === null || !("data" in response)) return {};
  const data: unknown = response.data;
  if (typeof data !== "object" || data === null) return {};
  return {
    status: "status" in data && typeof data.status === "boolean" ? data.status : undefined,
    errorMessage: "errorMessage" in data && typeof data.errorMessage === "string" ? data.errorMessage : undefined,
  };
};

function ConstituentContent({
  fileVersionId,
  workspaceId,
  accessPermission,
  hierarchyId,
  MergeName,
  projectId,
}: {
  fileVersionId?: string;
  workspaceId?: string;
  accessPermission?: Record<string, boolean>;
  hierarchyId?: string;
  MergeName?: string;
  projectId?: string;
}) {
  const { t } = useTranslation();
  const treeRef = useRef<{ refresh?: () => void } | null>(null);
  const [constituentFiles, setConstituentFiles] = useState<ConstituentRow[]>([]);
  // The grid's actions formatter is captured inside `columns` (useMemo with empty deps),
  // so handlers it closes over see stale state. Read via ref to always get the latest.
  const constituentFilesRef = useRef<ConstituentRow[]>([]);
  constituentFilesRef.current = constituentFiles;
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Recursively fetch constituent files.
   * Top-level items get __parentId = null, children get __parentId = their parent's _id.
   */
  const fetchAllConstituentFiles = async (
    parentVersionId: string,
    parentId: string | null = null,
  ): Promise<ConstituentRow[]> => {
    const query = new QueryBuilder().where("versions._id", "equal", parentVersionId);
    // `ExecuteQueryResult` declares `result` as required, but nothing validates the response on the
    // way in, so it is read through an optional member (as the other `executeQuery` call sites do)
    // and the fallback below stays live.
    const result: { result?: ConstituentRow[] } = await executeQuery<ConstituentRow>(
      constituentFilesOfMergedFileUrl,
      query,
    );
    const items = result.result || [];

    const allItems: ConstituentRow[] = [];
    for (const item of items) {
      allItems.push({ ...item, __parentId: parentId });
      if (item.hasChild) {
        const children = await fetchAllConstituentFiles(item._id!, item._id);
        allItems.push(...children);
      }
    }
    return allItems;
  };

  const refreshConstituentFiles = () => {
    fetchAllConstituentFiles(fileVersionId!)
      .then((result) => {
        setConstituentFiles(result);
      })
      .catch((e: unknown) => {
        console.log(e);
        Promise.reject(e);
      });
  };

  // Fetch on mount and when fileVersionId changes
  useEffect(() => {
    refreshConstituentFiles();
  }, [fileVersionId]);

  const handleFileSelectedCancelClick = () => {
    setIsDialogVisible(false);
  };
  const handleFileSelectedButtonClick = async (selectedFiles: Record<string, ConstituentRow>) => {
    // Only use top-level file IDs for the merge operation
    const topLevelFiles = constituentFiles.filter((f: ConstituentRow) => !f.__parentId);
    const fileVersionIds = topLevelFiles.map((file: ConstituentRow) => file._id);
    const sourceFileVersionIds = Object.values(selectedFiles).map((val: ConstituentRow) =>
      val.isEmptyContainer ? val.id : val.fileVersion,
    );
    const fileObjArray = Object.values(selectedFiles);
    // `isMergeAllowed` answers `{ mergedAllowed, isFile }`. Read the flag, not the object: the whole
    // object is always truthy, which left the "format not supported" branch below unreachable.
    const { mergedAllowed } = isMergeAllowed(fileObjArray);
    const params = {
      sourceFileVersionId: [...fileVersionIds, ...sourceFileVersionIds],
      id: hierarchyId,
      originalName: MergeName,
      spaceId: workspaceId,
    };
    try {
      if (!mergedAllowed) {
        dispatch(fileFormatNotSupportedToast());
      } else {
        const response = readStatusEnvelope(await createMergeFile(params));
        if (response.status) {
          const optimisticRows = fileObjArray.map((val: ConstituentRow) => ({
            _id: asRowId(val.isEmptyContainer ? val.id : val.fileVersion),
            fileName: val.name,
            resourceType: val.resourceType,
            fileVersionNumber: val.versionNumber ?? val.fileVersionNumber ?? 1,
            isDeleted: 0,
            conversionStatus: val.conversionStatus,
            projectId: val.project?._id ?? projectId,
            hasChild: val.resourceType === "RESOURCE_TYPE_MERGED_FILE",
            __parentId: null,
          }));
          setConstituentFiles((current) => [...current, ...optimisticRows]);
          dispatch(refreshFileManager(true));
          dispatch(getFileVersions({ resourceType: "RESOURCE_TYPE_MERGED_FILE", fileId: hierarchyId! }));
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_SUCCESS,
              content: t("Added successfully."),
            }),
          );
        } else {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_WARNING,
              content: untranslated(response.errorMessage!),
            }),
          );
        }
        handleFileSelectedCancelClick();
      }
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };
  const handleRemoveFilemerged = async (file: ConstituentRow) => {
    const { _id } = file;
    // Only use top-level file IDs for the merge operation
    const topLevelFiles = constituentFilesRef.current.filter((f: ConstituentRow) => !f.__parentId);
    const sourceFileVersionId = topLevelFiles
      .filter((f: ConstituentRow) => f._id !== _id)
      .map((f: ConstituentRow) => f._id);
    const params = {
      sourceFileVersionId,
      id: hierarchyId,
      originalName: MergeName,
      spaceId: workspaceId,
    };
    try {
      await createMergeFile(params);
      dispatch(refreshFileManager(true));
      dispatch(getFileVersions({ resourceType: "RESOURCE_TYPE_MERGED_FILE", fileId: hierarchyId! }));
      setConstituentFiles((current) => current.filter((f: ConstituentRow) => f._id !== _id && f.__parentId !== _id));
      dispatch(
        showToastNotification({
          type: TOAST_TYPE_SUCCESS,
          content: t("Removed successfully."),
        }),
      );
    } catch (error) {
      console.log(error);
      Promise.reject(error);
    }
  };
  const handleOpenLink = (file: ConstituentRow) => {
    const location = getViewerUrl({
      fileVersionId: file._id,
      spaceId: workspaceId,
      projectId: file.projectId,
    });
    navigate(location!);
  };
  const getPopOverItems = (file: ConstituentRow) => {
    const items: { text: string; id: string; disabled?: boolean }[] = [];
    items.push({
      text: "Open",
      id: "open",
      disabled: file.isDeleted === 1 || file.fileVersionNumber == 0,
    });
    // Only top-level items (no parent) can be removed from the merge
    if (!file.__parentId) {
      items.push({
        text: "Remove",
        id: "remove",
      });
    }
    return items;
  };
  const handleSelectedOption = async (args: { item: { properties?: { id?: string } } }, file: ConstituentRow) => {
    if (args.item.properties!.id == "open") {
      handleOpenLink(file);
    } else if (args.item.properties!.id == "remove") {
      await handleRemoveFilemerged(file);
    }
  };

  const actionsFormatter = (_row: number, _cell: number, _value: unknown, _col: unknown, data: ConstituentRow) => {
    if (!(["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"] as (string | undefined)[]).includes(data.resourceType)) {
      return document.createElement("div");
    }
    const container = document.createElement("div");
    container.className = "flex mt-px";
    const root = createRoot(container);
    root.render(
      <SelectDropDown
        select={(args) => handleSelectedOption(args, data)}
        items={getPopOverItems(data)}
        cssClass="e-caret-hide border-none bg-white p-0"
      >
        <BsThreeDotsVertical />
      </SelectDropDown>,
    );
    return container;
  };

  const versionFormatter = (_row: number, _cell: number, _value: unknown, _col: unknown, data: ConstituentRow) => {
    const container = document.createElement("div");
    container.className = "flex gap-2 items-center";
    const root = createRoot(container);
    root.render(
      <>
        <span>{`V${data.fileVersionNumber}`}</span>
        {data.isDeleted === 1 && (
          <ToolTipComponent showTipPointer position="bottom" content={t("This file is missing")}>
            <span>
              <RiErrorWarningFill className="text-red-500" size={16} />
            </span>
          </ToolTipComponent>
        )}
        {/* The template only reads `conversionStatus`, and returns "" for any value outside its
            closed set of status constants — so an unset status renders exactly as it does today. */}
        <span>{conversionStatusTemplate({ conversionStatus: data.conversionStatus ?? "" })}</span>
      </>,
    );
    return container;
  };

  const columns = useMemo(
    () => [
      {
        id: "actions",
        field: "resourceType",
        name: "",
        maxWidth: 40,
        formatter: actionsFormatter,
        resizable: false,
      },
      {
        id: "fileName",
        field: "fileName",
        name: t("File Name"),
        formatter: Formatters.tree,
      },
      {
        id: "version",
        field: "fileVersionNumber",
        name: t("Version"),
        maxWidth: 120,
        formatter: versionFormatter,
      },
    ],

    [],
  );

  const showToast = () => {
    dispatch(
      showToastNotification({
        content: t("Insufficient permission to perform this action."),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };
  const handleAddBtnClick = () => {
    if (!accessPermission?.write) {
      showToast();
      return;
    }
    setIsDialogVisible(true);
  };
  return (
    <>
      {isDialogVisible && (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileSelectedCancelClick}
          workspaceId={workspaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          heading={`Add Files in ${MergeName}`}
          disableFiles={constituentFiles
            .map((file: ConstituentRow): string | undefined => file.fileVersionId)
            .concat(fileVersionId)}
          addEmptyContainerToList
          minimumFiles={1}
        />
      )}
      <div>
        <div>
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleAddBtnClick}>
            {t("Add")}
          </ButtonComponent>
        </div>
        <div className="pt-4">
          {constituentFiles.length > 0 && (
            <TreeViewComponent
              ref={treeRef}
              treeData={constituentFiles}
              columns={columns}
              columnId="fileName"
              identifierPropName="_id"
              parentPropName="__parentId"
              disableHeader
              height={400}
            />
          )}
        </div>
      </div>
    </>
  );
}
export default ConstituentContent;
